// Job Database (admin) — every service across all weeks: search, community/week/tech
// filters, status chips. Tapping a row's MAIN area opens the read-only detail (v104);
// Edit/Delete live in their own column so a mis-tap can never destroy anything.
import { useComputed, useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { apiGet } from '../../lib/api';
import { schedAction } from '../../state/admin';
import type { JobRow } from '../../state/adminUi';
import { editSheet, jobDetail, jobsRefresh, showJobsDb } from '../../state/adminUi';
import { loadWeeks } from '../../state/schedule';
import { toast } from '../../state/toast';
import { AdminSheet } from './AdminSheet';

interface JobsRes {
  total: number;
  default: string;
  weeks: string[];
  jobs: JobRow[];
}

export function StatusBadge({ status }: { status: JobRow['status'] }) {
  if (status === 'done') return <span class="db-badge bg-emerald-100 text-done-ink">Done</span>;
  if (status === 'missed') return <span class="db-badge bg-red-100 text-missed-ink">Missed</span>;
  return <span class="db-badge bg-slate-200 text-slate-700">Open</span>;
}

export function JobsDbSheet() {
  const close = () => (showJobsDb.value = false);
  const jobs = useSignal<JobRow[]>([]);
  const weeksList = useSignal<string[]>([]);
  const defaultWeek = useSignal('');
  const state = useSignal<'loading' | 'ready' | 'error'>('loading');
  const q = useSignal('');
  const fDev = useSignal('');
  const fWeek = useSignal('');
  const fTech = useSignal('');
  const fStatus = useSignal<'all' | 'active' | 'done' | 'missed'>('all');

  async function fetchJobs() {
    state.value = jobs.value.length ? 'ready' : 'loading';
    try {
      const res = await apiGet<JobsRes>('/api/jobs');
      jobs.value = res.jobs;
      weeksList.value = res.weeks;
      defaultWeek.value = res.default;
      state.value = 'ready';
    } catch {
      state.value = 'error';
    }
  }

  useEffect(() => {
    void fetchJobs();
  }, [jobsRefresh.value]);

  const filtered = useComputed(() => {
    const needle = q.value.trim().toLowerCase();
    return jobs.value.filter((j) => {
      if (fStatus.value !== 'all' && j.status !== fStatus.value) return false;
      if (fDev.value && j.development !== fDev.value) return false;
      if (fWeek.value && j.weekOf !== fWeek.value) return false;
      if (fTech.value && j.tech !== fTech.value) return false;
      if (needle) {
        const hay = [
          j.development,
          j.lot,
          j.address,
          j.name,
          j.phone,
          j.problem,
          j.tech,
          j.instructions,
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  });

  const devs = useComputed(() =>
    [...new Set(jobs.value.map((j) => j.development).filter(Boolean))].sort(),
  );
  const techs = useComputed(() =>
    [...new Set(jobs.value.map((j) => j.tech).filter(Boolean))].sort(),
  );

  function addService() {
    editSheet.value = {
      mode: 'add',
      call: { slot: '8–12' },
      day: 'mon',
      weekOf: defaultWeek.value || weeksList.value[0] || '',
      fromDb: true,
    };
  }

  function editJob(j: JobRow) {
    editSheet.value = {
      mode: 'edit',
      id: j.id,
      call: j,
      day: j.day,
      weekOf: j.weekOf,
      fromDb: true,
    };
  }

  async function deleteJob(j: JobRow) {
    const who = (j.development ? `${j.development} — ` : '') + (j.address || 'this call');
    if (!confirm(`Delete ${who} (${j.weekOf})? This cannot be undone.`)) return;
    const ok = await schedAction({ action: 'deleteCall', id: j.id, weekOf: j.weekOf });
    if (ok) {
      toast('Deleted.');
      jobsRefresh.value++;
      void loadWeeks().catch(() => {});
    }
  }

  const selCls =
    'tap min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm';
  const chips: { key: typeof fStatus.value; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Open' },
    { key: 'done', label: 'Done' },
    { key: 'missed', label: 'Missed' },
  ];

  return (
    <AdminSheet
      title="Job Database"
      sub="Every service across all weeks — search, filter, add or remove."
      onClose={close}
      tall
      testId="jobs-db-sheet"
    >
      <div class="flex gap-2 pb-2">
        <input
          type="search"
          class="tap w-full rounded-xl border border-slate-300 px-3 py-2"
          placeholder="Search address, name, lot, problem, tech…"
          aria-label="Search jobs"
          value={q.value}
          onInput={(e) => (q.value = (e.target as HTMLInputElement).value)}
        />
        <button class="btn-primary shrink-0" data-testid="db-add-service" onClick={addService}>
          ＋ Add
        </button>
      </div>
      <div class="flex gap-2 pb-2">
        <select
          class={selCls}
          aria-label="Filter by community"
          value={fDev.value}
          onChange={(e) => (fDev.value = (e.target as HTMLSelectElement).value)}
        >
          <option value="">All communities</option>
          {devs.value.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <select
          class={selCls}
          aria-label="Filter by week"
          value={fWeek.value}
          onChange={(e) => (fWeek.value = (e.target as HTMLSelectElement).value)}
        >
          <option value="">All weeks</option>
          {weeksList.value.map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </select>
        <select
          class={selCls}
          aria-label="Filter by tech"
          value={fTech.value}
          onChange={(e) => (fTech.value = (e.target as HTMLSelectElement).value)}
        >
          <option value="">All techs</option>
          {techs.value.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div class="flex gap-1.5 pb-2" role="group" aria-label="Status filter">
        {chips.map((c) => (
          <button
            key={c.key}
            class={`tap rounded-full px-3 py-1 text-sm font-semibold ${fStatus.value === c.key ? 'bg-navy-900 text-white' : 'bg-slate-200 text-slate-700'}`}
            aria-pressed={fStatus.value === c.key}
            onClick={() => (fStatus.value = c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>
      <p class="pb-2 text-xs font-semibold text-slate-500" data-testid="db-count">
        {filtered.value.length} of {jobs.value.length} service{jobs.value.length === 1 ? '' : 's'}
      </p>

      {state.value === 'loading' && <p class="p-6 text-center text-slate-500">Loading…</p>}
      {state.value === 'error' && (
        <div class="p-6 text-center">
          <p class="text-slate-500">Could not load jobs.</p>
          <button class="btn-ghost mt-2" onClick={() => void fetchJobs()}>
            Retry
          </button>
        </div>
      )}
      {state.value === 'ready' && filtered.value.length === 0 && (
        <p class="p-6 text-center text-slate-500">No matching services.</p>
      )}

      <div class="space-y-2">
        {filtered.value.map((j) => (
          <div
            key={`${j.weekOf}-${j.id}`}
            class="flex items-stretch gap-2 rounded-xl border border-slate-200 bg-white p-2"
            data-testid="db-row"
          >
            <button
              type="button"
              class="tap min-w-0 flex-1 rounded-lg p-1 text-left"
              aria-label={`View full details for ${j.development || j.address || 'service call'}`}
              onClick={() => (jobDetail.value = j)}
            >
              <div class="flex flex-wrap items-center gap-1.5">
                <StatusBadge status={j.status} />
                {j.needsReturn && <span class="db-badge bg-note-amber text-note-ink">Return</span>}
                <span class="truncate font-bold text-navy-900">
                  {j.development || j.address || 'Service Call'}
                </span>
                {j.tech && (
                  <span class="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-800">
                    {j.tech}
                  </span>
                )}
              </div>
              {(j.development && j.address) || j.lot || j.name ? (
                <p class="mt-0.5 truncate text-sm text-slate-600">
                  {[
                    j.development && j.address ? j.address : '',
                    j.lot ? `Lot ${j.lot}` : '',
                    j.name,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ) : null}
              {j.problem && <p class="mt-0.5 line-clamp-2 text-sm text-slate-500">{j.problem}</p>}
              <p class="mt-0.5 text-xs font-semibold text-slate-400">
                {j.weekOf}
                {j.dayLabel ? ` · ${j.dayLabel.split(',')[0]}` : ''}
                {j.slot ? ` · ${j.slot}` : ''}
              </p>
            </button>
            <div class="flex shrink-0 flex-col justify-center gap-1.5">
              <button class="mini-btn" onClick={() => editJob(j)}>
                Edit
              </button>
              <button class="mini-btn text-missed-ink" onClick={() => void deleteJob(j)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </AdminSheet>
  );
}
